import React, { useState, useEffect } from "react";

interface AddToCompareProps {
  productId: string;
  className?: string;
  buttonText?: string;
  removeButtonText?: string;
  isInCompareList?: boolean;
  compareFull?: boolean;
}

export const AddToCompare: React.FC<AddToCompareProps> = ({
  productId,
  buttonText = "Usporedi",
  removeButtonText = "Ukloni",
  isInCompareList: initialIsInCompareList = false,
  compareFull: initialCompareFull = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInCompareList, setIsInCompareList] = useState(
    initialIsInCompareList
  );
  const [compareFull, setCompareFull] = useState(initialCompareFull);

  // Update isInCompareList when the prop changes
  useEffect(() => {
    setIsInCompareList(initialIsInCompareList);
  }, [initialIsInCompareList]);

  // Update compareFull when the prop changes
  useEffect(() => {
    setCompareFull(initialCompareFull);
  }, [initialCompareFull]);

  // Listen for compare list full event
  useEffect(() => {
    const handleCompareFullEvent = (event: CustomEvent) => {
      if (event.detail && typeof event.detail.full === "boolean") {
        setCompareFull(event.detail.full);
      }
    };

    // Add event listener
    document.addEventListener(
      "add-to-compare-full",
      handleCompareFullEvent as EventListener
    );

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener(
        "add-to-compare-full",
        handleCompareFullEvent as EventListener
      );
    };
  }, []);

  // Check if product is already in compare list on component mount
  useEffect(() => {
    const checkCompareStatus = async () => {
      // Skip the check if we already have the initial state
      if (initialIsInCompareList !== undefined) {
        return;
      }

      try {
        const response = await fetch(
          `/usporedi/artikl/${productId}?_format=json`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "same-origin",
          }
        );

        if (response.ok) {
          const data = await response.json();
          setIsInCompareList(data.isInCompareList || false);
        }
      } catch (err) {
        console.error("Error checking compare status:", err);
      }
    };

    checkCompareStatus();
  }, [productId, initialIsInCompareList]);

  const handleToggleCompare = async () => {
    if (!productId) {
      setError("Product ID is required");
      return;
    }

    // Don't allow adding if compare list is full and product is not already in the list
    if (compareFull && !isInCompareList) {
      setError("Compare list is full. Please remove some items first.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = `/usporedi/artikl/${productId}?_format=json`;
      const method = isInCompareList ? "DELETE" : "PUT";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body:
          method === "PUT"
            ? JSON.stringify({ product_id: productId })
            : undefined,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${
            isInCompareList ? "remove from" : "add to"
          } compare list: ${response.status}`
        );
      }

      const data = await response.json();

      // Check if the compare list is full
      switch (data.result.toLowerCase()) {
        case "max allowed product limit reached": {
          if (!compareFull) {
            const event = new CustomEvent("add-to-compare-full", {
              detail: { full: true },
            });
            document.dispatchEvent(event);
          }
          return;
        }
        case "added": {
          setIsInCompareList(true);
          const event = new CustomEvent("add-to-compare", {
            detail: { product_id: productId },
          });
          document.dispatchEvent(event);
          if (!compareFull && data.full) {
            const event = new CustomEvent("add-to-compare-full", {
              detail: { full: true },
            });
            document.dispatchEvent(event);
          }
          break;
        }
        case "deleted": {
          setIsInCompareList(false);
          const event = new CustomEvent("add-to-compare", {
            detail: { product_id: productId },
          });
          document.dispatchEvent(event);
          if (compareFull) {
            const event = new CustomEvent("add-to-compare-full", {
              detail: { full: false },
            });
            document.dispatchEvent(event);
          }
          break;
        }
      }
    } catch (err) {
      console.error(
        `Error ${
          isInCompareList ? "removing from" : "adding to"
        } compare list:`,
        err
      );
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${
              isInCompareList ? "remove from" : "add to"
            } compare list`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Determine button class based on state
  const getButtonClass = () => {
    // const baseClass =
    //   "product-compare-add";

    if (isInCompareList) {
      // When product is in compare list - red background
      return 'product-compare-remove';
    } else {
      // When product is not in compare list - green background
      return "product-compare-add";
    }
  };

  return (
    <div className="product-compare-add-container">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleToggleCompare();
        }}
        disabled={isLoading || (compareFull && !isInCompareList)}
        className={getButtonClass()}
      >
        {isLoading ? (
          <span className="flex items-top">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-black"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {isInCompareList ? "..." : "..."}
          </span>
        ) : isInCompareList ? (
          removeButtonText
        ) : (
          buttonText
        )}
      </button>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};
